import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import portalApi, { getPortalErrorMessage } from '@/lib/portalApi';
import { usePortalAuth } from '@/lib/portalAuth';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import {
  Send, User, Phone, Mail, MapPin, FileText, AlertTriangle, CheckCircle,
  DollarSign, Heart, Clock
} from 'lucide-react';
import { useBranding } from '@/lib/branding';

const FUNDING_TYPES = [
  { value: 'SELF_MANAGED', label: 'Self Managed' },
  { value: 'NDIA_MANAGED', label: 'NDIA Managed' },
  { value: 'PLAN_MANAGED', label: 'Plan Managed' },
  { value: 'SIL_MANAGED', label: 'SIL Managed' },
  { value: 'PRIVATE', label: 'Private/Self-Funded' },
  { value: 'UNKNOWN', label: 'Not Sure / To Be Determined' },
];

const URGENCY_LEVELS = [
  { value: 'LOW', label: 'Low - Flexible timing', color: 'text-gray-500' },
  { value: 'MEDIUM', label: 'Medium - Within 2-4 weeks', color: 'text-amber-500' },
  { value: 'HIGH', label: 'High - Urgent need', color: 'text-red-500' },
];

export default function PortalReferralPage() {
  const { organisationName } = useBranding();
  const { user } = usePortalAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [referralNo, setReferralNo] = useState('');

  const [formData, setFormData] = useState({
    participantName: '',
    dateOfBirth: '',
    phone: '',
    email: '',
    ndisNumber: '',
    fundingType: 'UNKNOWN',
    supportNeeds: '',
    locationPreference: '',
    urgencyLevel: 'MEDIUM',
    notes: '',
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.participantName.trim()) {
      toast.error('Participant name is required');
      return;
    }
    if (!formData.supportNeeds.trim()) {
      toast.error('Please describe the support needs');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        participantName: formData.participantName.trim(),
        dateOfBirth: formData.dateOfBirth || null,
        phone: formData.phone || null,
        email: formData.email || null,
        ndisNumber: formData.ndisNumber || null,
        fundingType: formData.fundingType,
        supportNeeds: formData.supportNeeds.trim(),
        locationPreference: formData.locationPreference || null,
        urgencyLevel: formData.urgencyLevel,
        notes: formData.notes || null,
      };

      const res = await portalApi.post('/referrals', payload);
      setReferralNo(res.data.data?.referralNo || 'Submitted');
      setSubmitted(true);
      toast.success('Referral submitted successfully');
    } catch (err) {
      toast.error(getPortalErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto">
        <Card className="bg-green-500/5 border-green-500/20">
          <CardContent className="py-12 text-center">
            <div className="h-16 w-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Referral Submitted</h2>
            <p className="text-muted-foreground mb-4">
              Your referral has been received and will be reviewed by our team.
            </p>
            <div className="bg-background/50 rounded-lg p-4 mb-6 inline-block">
              <p className="text-sm text-muted-foreground">Reference Number</p>
              <p className="text-xl font-mono font-bold">{referralNo}</p>
            </div>
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                You will be contacted within 2-3 business days regarding the next steps.
              </p>
              <Button onClick={() => {
                setSubmitted(false);
                setFormData({
                  participantName: '',
                  dateOfBirth: '',
                  phone: '',
                  email: '',
                  ndisNumber: '',
                  fundingType: 'UNKNOWN',
                  supportNeeds: '',
                  locationPreference: '',
                  urgencyLevel: 'MEDIUM',
                  notes: '',
                });
              }} variant="outline" data-testid="submit-another-btn">
                Submit Another Referral
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-2xl font-bold">Submit a Referral</h1>
        <p className="text-muted-foreground">
          Refer someone who may benefit from our disability support services
        </p>
      </div>

      {/* Info Banner */}
      <Alert>
        <Heart className="h-4 w-4" />
        <AlertDescription>
          Thank you for thinking of {organisationName}. We provide high-quality NDIS support services.
          Please fill out the form below and we'll be in touch soon.
        </AlertDescription>
      </Alert>

      {/* Referral Form */}
      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Participant Details
            </CardTitle>
            <CardDescription>Information about the person being referred</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="participantName">Full Name *</Label>
                <Input
                  id="participantName"
                  value={formData.participantName}
                  onChange={(e) => handleChange('participantName', e.target.value)}
                  placeholder="Enter participant's full name"
                  required
                  data-testid="participant-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="dateOfBirth">Date of Birth</Label>
                <Input
                  id="dateOfBirth"
                  type="date"
                  value={formData.dateOfBirth}
                  onChange={(e) => handleChange('dateOfBirth', e.target.value)}
                  data-testid="date-of-birth"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    placeholder="04XX XXX XXX"
                    className="pl-9"
                    data-testid="phone"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="email@example.com"
                    className="pl-9"
                    data-testid="email"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="locationPreference">Location / Suburb</Label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="locationPreference"
                  value={formData.locationPreference}
                  onChange={(e) => handleChange('locationPreference', e.target.value)}
                  placeholder="Preferred service location"
                  className="pl-9"
                  data-testid="location"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="mt-4">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Funding Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="fundingType">Funding Type *</Label>
                <Select
                  value={formData.fundingType}
                  onValueChange={(value) => handleChange('fundingType', value)}
                >
                  <SelectTrigger data-testid="funding-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FUNDING_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="ndisNumber">NDIS Number (if known)</Label>
                <Input
                  id="ndisNumber"
                  value={formData.ndisNumber}
                  onChange={(e) => handleChange('ndisNumber', e.target.value)}
                  placeholder="e.g., 123456789"
                  data-testid="ndis-number"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="mt-4">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Support Requirements
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="supportNeeds">Support Needs *</Label>
              <Textarea
                id="supportNeeds"
                value={formData.supportNeeds}
                onChange={(e) => handleChange('supportNeeds', e.target.value)}
                placeholder="Please describe the type of support required, including any specific needs or preferences..."
                rows={4}
                required
                data-testid="support-needs"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="urgencyLevel">Urgency Level</Label>
              <Select
                value={formData.urgencyLevel}
                onValueChange={(value) => handleChange('urgencyLevel', value)}
              >
                <SelectTrigger data-testid="urgency-level">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {URGENCY_LEVELS.map((level) => (
                    <SelectItem key={level.value} value={level.value}>
                      <span className={level.color}>{level.label}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Additional Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                placeholder="Any other information that may be helpful..."
                rows={3}
                data-testid="notes"
              />
            </div>
          </CardContent>
        </Card>

        {/* Referrer Info */}
        <Card className="mt-4 bg-muted/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <User className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">Submitting as</p>
                <p className="text-sm text-muted-foreground">
                  {user?.firstName} {user?.lastName} ({user?.email})
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Submit */}
        <div className="flex justify-end gap-3 mt-6">
          <Button
            type="submit"
            size="lg"
            disabled={loading}
            data-testid="submit-referral-btn"
          >
            {loading ? 'Submitting...' : 'Submit Referral'}
            <Send className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </form>
    </div>
  );
}
